/**
 * Roster Manager (FR-ROSTER-01/02/03, AC-ROSTER-001 … AC-ROSTER-006).
 *
 * One screen, four jobs:
 *   1. the roster table — server-side search, cohort filter, sort by jersey /
 *      name / cohort / position, and paging, all held in the URL so a refresh
 *      or a shared link restores the exact view;
 *   2. add / edit through `PlayerForm` (a dialog over the table), whose cohort dropdown is fed
 *      by the tenant catalog and can extend it without leaving the flow;
 *   3. delete — one row at a time or a multi-select batch, both behind
 *      `RosterDeleteDialog`, which names what disappears;
 *   4. the states a list is not finished without: first-run, no-matches,
 *      loading, error-with-retry, and a card layout at 375px.
 *
 * RBAC mirrors the gateway: coaching staff + Team Admin may add and edit
 * (`ROSTER_WRITE_PERSONAS`), only administrators may delete players or extend
 * the cohort catalog. Everyone else gets the read-only table.
 */
import { useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useToast } from '../../app/toast-context';
import { DataTable } from '../../components/data-table';
import { PageHeader } from '../../components/page-header';
import type { CohortValue, Player, PlayerInput } from '../../lib/api-types';
import type { Persona } from '../../lib/personas';
import { TableExportButton } from '../integrations/export-dialog';

import { PlayerForm } from './player-form';
import { buildRosterColumns } from './roster-columns';
import { RosterDeleteDialog } from './roster-delete-dialogs';
import { buildRosterExportSource } from './roster-export';
import {
  useCohortCatalog,
  usePlayerSelection,
  useRoster,
  type PlayerSortField,
} from './use-roster';

/** Personas allowed to add/edit a roster row (roster-service ROSTER_WRITE_PERSONAS). */
const ROSTER_WRITE_PERSONAS: readonly Persona[] = [
  'platform_admin',
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
];
/** Deleting players and extending the cohort catalog are administrator-only. */
const ROSTER_ADMIN_PERSONAS: readonly Persona[] = ['platform_admin', 'team_admin'];

type OpenPanel =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; player: Player }
  | { kind: 'delete'; players: readonly Player[] };

export function RosterManagerPage(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { persona } = useSession();
  const roster = useRoster();
  const catalog = useCohortCatalog();
  const selection = usePlayerSelection();
  const [panel, setPanel] = useState<OpenPanel>({ kind: 'none' });

  const canWrite = persona !== null && ROSTER_WRITE_PERSONAS.includes(persona);
  const canAdminister = persona !== null && ROSTER_ADMIN_PERSONAS.includes(persona);
  const isEveryRowSelected =
    roster.players.length > 0 &&
    roster.players.every((player) => selection.ids.has(player.player_id));

  function closeAndReload(message: string): void {
    setPanel({ kind: 'none' });
    showToast(message, 'success');
    roster.reload();
  }

  function savePlayer(input: PlayerInput): Promise<Player> {
    return panel.kind === 'edit'
      ? apiClient.updatePlayer(panel.player.player_id, input)
      : apiClient.createPlayer(input);
  }

  function registerCohortValue(value: CohortValue): void {
    catalog.register(value);
    showToast(`Cohort “${value.label}” added to your catalog.`, 'success');
  }

  function onPlayerSaved(saved: Player, options: { isAnother: boolean }): void {
    if (!options.isAnother) {
      closeAndReload(
        panel.kind === 'edit' ? `${saved.full_name} updated.` : `${saved.full_name} added.`
      );
      return;
    }
    showToast(`${saved.full_name} added. Add the next player.`, 'success');
    roster.reload();
  }

  const openCreate = (): void => setPanel({ kind: 'create' });

  return (
    <section className="stack roster">
      <PageHeader
        title="Roster"
        context={
          <>
            {roster.total} {roster.total === 1 ? 'player' : 'players'} · cohort is required on every
            player and replaces date of birth.
          </>
        }
        actions={
          <>
            <TableExportButton
              isDisabled={roster.total === 0}
              source={() => buildRosterExportSource(apiClient, roster.query)}
            />
            {canWrite ? (
              <button type="button" className="btn btn--primary" onClick={openCreate}>
                Add Player
              </button>
            ) : null}
          </>
        }
      />

      {catalog.error !== null ? (
        <div className="banner banner--warning" role="alert">
          <p>{catalog.error}</p>
          <button type="button" className="btn btn--secondary btn--sm" onClick={catalog.reload}>
            Retry cohort catalog
          </button>
        </div>
      ) : null}

      <DataTable<Player>
        caption="Roster"
        surface="roster"
        layoutName="Roster columns"
        columns={buildRosterColumns({
          selectedIds: selection.ids,
          onToggle: selection.toggle,
          canSelect: canAdminister,
        })}
        rows={roster.players}
        getRowId={(player) => player.player_id}
        getRowClassName={(player) =>
          selection.ids.has(player.player_id) ? 'data-table__row--selected' : undefined
        }
        sort={{ by: roster.query.sortBy, order: roster.query.sortOrder }}
        onSortChange={(sort) =>
          roster.setQuery({ sortBy: sort.by as PlayerSortField, sortOrder: sort.order })
        }
        search={{
          value: roster.query.search,
          onChange: (value) => roster.setQuery({ search: value }),
          // The service's `q` matches first/last/full name and jersey number;
          // cohort has its own filter, so the label does not over-promise.
          label: 'Search by name or jersey',
          placeholder: 'Search name or jersey',
        }}
        filters={[
          {
            id: 'cohort',
            label: 'Cohort',
            value: roster.query.cohort,
            defaultValue: 'all',
            options: [
              { value: 'all', label: 'All cohorts' },
              ...catalog.values.map((value) => ({
                value: value.code,
                label: `${value.label} (${value.code})${value.is_deprecated ? ' — retired' : ''}`,
              })),
            ],
            onChange: (value) => roster.setQuery({ cohort: value }),
          },
        ]}
        pagination={{
          page: roster.query.page,
          pageSize: roster.query.pageSize,
          total: roster.total,
          onPageChange: (page) => roster.setQuery({ page }),
          onPageSizeChange: (pageSize) => roster.setQuery({ pageSize }),
        }}
        toolbarActions={
          canAdminister ? (
            <>
              <button
                type="button"
                className="dt-tool roster__select-all"
                aria-pressed={isEveryRowSelected}
                disabled={roster.players.length === 0}
                onClick={() => selection.togglePage(roster.players, !isEveryRowSelected)}
              >
                {isEveryRowSelected ? 'Clear page selection' : 'Select all on this page'}
              </button>
              {selection.players.length > 0 ? (
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  onClick={() => setPanel({ kind: 'delete', players: selection.players })}
                >
                  Delete {selection.players.length} selected
                </button>
              ) : null}
            </>
          ) : undefined
        }
        isLoading={roster.isLoading}
        error={roster.error}
        onRetry={roster.reload}
        emptyTitle={roster.isUnfiltered ? 'No players yet' : 'No players match these filters'}
        emptyDescription={
          roster.isUnfiltered
            ? 'Add your first player to start tracking plays against a roster.'
            : 'Try a different search term or clear the cohort filter.'
        }
        emptyAction={
          roster.isUnfiltered ? (
            canWrite ? (
              <button type="button" className="btn btn--primary btn--sm" onClick={openCreate}>
                Add your first player
              </button>
            ) : null
          ) : (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => roster.setQuery({ search: '', cohort: 'all' })}
            >
              Clear filters
            </button>
          )
        }
        actionsWidth={canAdminister ? 104 : 72}
        primaryRowAction={
          canWrite
            ? (player) => (
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => setPanel({ kind: 'edit', player })}
                >
                  Edit
                </button>
              )
            : undefined
        }
        rowMenu={
          canAdminister
            ? (player) => [
                {
                  id: 'delete',
                  label: 'Delete',
                  tone: 'danger',
                  onSelect: () => setPanel({ kind: 'delete', players: [player] }),
                },
              ]
            : undefined
        }
        getRowMenuLabel={(player) => `More actions for ${player.full_name}`}
      />

      {panel.kind === 'create' || panel.kind === 'edit' ? (
        <PlayerForm
          player={panel.kind === 'edit' ? panel.player : null}
          cohortValues={catalog.values}
          canAddCohortValue={canAdminister}
          onCohortValueCreated={registerCohortValue}
          onSave={savePlayer}
          onSaved={onPlayerSaved}
          onCancel={() => setPanel({ kind: 'none' })}
        />
      ) : null}

      {panel.kind === 'delete' ? (
        <RosterDeleteDialog
          players={panel.players}
          onCancel={() => setPanel({ kind: 'none' })}
          onDeleted={(removedCount) => {
            selection.clear();
            closeAndReload(
              `${removedCount} ${removedCount === 1 ? 'player' : 'players'} removed from the roster.`
            );
          }}
        />
      ) : null}
    </section>
  );
}
